/**
 * "Suggest an idea" — writes consultantFeedback (category missing_feature by
 * default) through the existing feedbackService, with severity choice.
 */
import React from 'react';
import { CheckCircle, Lightbulb, Loader2, Send } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { submitFeedback } from '../../../services/feedbackService';
import {
  FEEDBACK_SEVERITY_LABELS,
  type FeedbackSeverity,
} from '../../../types/feedback.types';

export const IdeaTab: React.FC = () => {
  const { userProfile } = useAuth();
  const { showToast } = useToast();
  const [description, setDescription] = React.useState('');
  const [whereInSystem, setWhereInSystem] = React.useState('');
  const [severity, setSeverity] = React.useState<FeedbackSeverity>('low');
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (): Promise<void> => {
    if (!userProfile) return;
    if (!description.trim()) return void showToast('error', 'Describe your idea first');
    setSubmitting(true);
    try {
      await submitFeedback(
        {
          whereInSystem: whereInSystem.trim() || window.location.pathname,
          category: 'missing_feature',
          severity,
          description: description.trim(),
          screenshot: null,
        },
        userProfile,
        window.location.href,
        navigator.userAgent,
      );
      setDone(true);
      showToast('success', 'Idea sent', 'Thanks — the support team reviews every suggestion.');
    } catch (err) {
      showToast('error', 'Could not send idea', (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <CheckCircle className="h-10 w-10 text-green-500" />
        <p className="text-lg font-semibold text-neutral-900">Idea sent — thank you!</p>
        <button
          type="button"
          onClick={() => {
            setDone(false);
            setDescription('');
          }}
          className="secondary-button text-sm"
        >
          Suggest another
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
        <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" />
        Wish the system did something it doesn't? Tell us — suggestions land directly in the
        support queue.
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-neutral-600">
          Where in the system? <span className="text-neutral-400">(optional)</span>
        </label>
        <input
          type="text"
          value={whereInSystem}
          disabled={submitting}
          onChange={(e) => setWhereInSystem(e.target.value)}
          placeholder="e.g. CRM contact page, Deal capture…"
          className="input-field text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-neutral-600">Your idea</label>
        <textarea
          rows={4}
          value={description}
          disabled={submitting}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What should the system do, and how would it help you?"
          className="input-field text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-neutral-600">How much would it help?</label>
        <select
          value={severity}
          disabled={submitting}
          onChange={(e) => setSeverity(e.target.value as FeedbackSeverity)}
          className="input-field text-sm"
        >
          {(Object.keys(FEEDBACK_SEVERITY_LABELS) as FeedbackSeverity[]).map((s) => (
            <option key={s} value={s}>
              {FEEDBACK_SEVERITY_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          className="primary-button flex items-center gap-1.5 text-sm"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send idea
        </button>
      </div>
    </div>
  );
};

export default IdeaTab;
